import type { DateTime } from "luxon";
import { v4 as uuidv4 } from "uuid";
import type { Department } from "../contexts/AuthProvider";
import { calculateShiftTimes } from "../contexts/utils/shift-times";
import { formatPersonFullName } from "./formatters";
import type { EmploymentType, Nurse, NurseInShift, ShiftTimes, ShiftType } from "./models";

export type NurseShiftDraft = {
	nurse: Nurse;
	date: DateTime;
	shiftType: string;
	location: string | undefined;
	/** An add from the actual view records a punch-in only, so the shift starts and ends at its start. */
	addedFromActual: boolean;
	requestId: string | undefined;
	/** The caller's own hours; without them the department's default hours for the shift type. */
	shiftTimes: ShiftTimes | undefined;
	nurseInShiftId: string | undefined;
	shiftBonus: number | undefined;
	isDraftMode: boolean;
};

/** The shift an add puts `nurse` on, with the department's default hours unless the caller has its own. */
export const buildNurseShift = (
	{ nurse, date, shiftType, location, addedFromActual, requestId, shiftTimes, nurseInShiftId, shiftBonus, isDraftMode }: NurseShiftDraft,
	chosenDepartment: Department,
	isHospital: boolean,
	lastNameFirst: boolean,
): NurseInShift => {
	// The default times are read by the shift's position in the department's shift types.
	const nurseShiftTimes = shiftTimes
		? { startTime: shiftTimes.start, endTime: shiftTimes.end }
		: calculateShiftTimes({
				day: { date },
				shiftNo: chosenDepartment.shift_types.indexOf(shiftType as ShiftType),
				nurseRole: nurse.role,
				chosenDepartment,
				location,
			});
	const nurseToAdd: NurseInShift = {
		id: nurseInShiftId ?? uuidv4(),
		nurseId: nurse.id,
		departmentId: chosenDepartment.department_id,
		isPending: isDraftMode ? true : isHospital,
		isPendingToDelete: false,
		isPendingToSwap: false,
		isPublished: true,
		isPendingToMove: false,
		startTime: nurseShiftTimes.startTime,
		endTime: nurseShiftTimes.endTime,
		role: nurse.role,
		name: formatPersonFullName(nurse, lastNameFirst),
		charge: nurse.isShiftManager,
		type: nurse.employmentType as EmploymentType,
		location,
		// DEFAULT: the profile's single-shift add resolves its own type into `shiftTimes`; everyone else adds to the slot's.
		shiftType: shiftTimes?.shiftType ?? shiftType,
		notes: "",
		bonus: shiftBonus || 0,
		breaks: [],
		requestId,
		trainingStatus: nurse.trainingStatus,
		jobId: nurse.jobs?.[0]?.id,
		jobName: nurse.jobs?.[0]?.displayName,
	};
	if (addedFromActual) nurseToAdd.endTime = nurseToAdd.startTime;
	return nurseToAdd;
};
